import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { msalInstance } from '../authConfig';

type SessionState = 'active' | 'expired' | 'logged_out' | 'unauthenticated';

interface AuthContextType {
  accessToken: string | null;
  setAccessToken: (token: string | null) => void;
  acquireToken: () => Promise<void>;
  refreshToken: () => Promise<string | null>;
  forceLogout: () => void;
  softLogout: () => void;
  expireSession: () => void;
  initiateLogin: () => Promise<void>;
  sessionState: SessionState;
  sessionMessage: string | null;
  isRefreshing: boolean;
  isHandlingRedirect: boolean;
  error: string | null;
  clearError: () => void;
  showError: (message: string) => void;
  globalErrors: string[];
  dismissError: (index: number) => void;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);



interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider = ({ children }: AuthProviderProps) => {
  const [accessToken, setAccessToken] = useState<string | null>(sessionStorage.getItem('accessToken'));
  const [error, setError] = useState<string | null>(null);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [globalErrors, setGlobalErrors] = useState<string[]>([]);
  
  // Persist session state to handle page refreshes correctly
  const getInitialSessionState = (): SessionState => {
    const storedState = sessionStorage.getItem('sessionState');
    if (storedState) {
      return storedState as SessionState;
    }
    return sessionStorage.getItem('accessToken') ? 'active' : 'unauthenticated';
  };
  
  const [sessionState, setSessionStateInternal] = useState<SessionState>(getInitialSessionState());
  const [sessionMessage, setSessionMessage] = useState<string | null>(null);
  const [isHandlingRedirect, setIsHandlingRedirect] = useState(true);
  
  // Wrapper to persist session state changes
  const setSessionState = (state: SessionState) => {
    setSessionStateInternal(state);
    if (state === 'logged_out' || state === 'expired') {
      sessionStorage.setItem('sessionState', state);
    } else {
      sessionStorage.removeItem('sessionState');
    }
  };

  useEffect(() => {
    if (accessToken) {
      sessionStorage.setItem('accessToken', accessToken);
    } else {
      sessionStorage.removeItem('accessToken');
    }
  }, [accessToken]);

  // Function to acquire token
  const acquireToken = async () => {
    try {
      const accounts = msalInstance.getAllAccounts();
      if (accounts.length > 0) {
        const response = await msalInstance.acquireTokenSilent({
          account: accounts[0],
          scopes: [process.env.REACT_APP_AD_SCOPE!]
        });
        setAccessToken(response.accessToken);
        setError(null);
      }
    } catch (error) {
      setError("Error acquiring access token");
      console.error("Error acquiring access token:", error);
      setAccessToken(null);
    }
  };

  // Function to refresh token when it expires
  const refreshToken = async (): Promise<string | null> => {
    if (isRefreshing) {
      // If already refreshing, wait and return current token
      return accessToken;
    }

    setIsRefreshing(true);
    setError(null);
    
    try {
      const accounts = msalInstance.getAllAccounts();
      
      if (accounts.length === 0) {
        forceLogout();
        return null;
      }

      const response = await msalInstance.acquireTokenSilent({
        account: accounts[0],
        scopes: [process.env.REACT_APP_AD_SCOPE!],
        forceRefresh: true
      });

      setAccessToken(response.accessToken);
      setIsRefreshing(false);
      return response.accessToken;
      
    } catch (error) {
      setError('Session expired. Please log in again.');
      setIsRefreshing(false);
      
      // If refresh fails, force logout
      setTimeout(() => forceLogout(), 1000);
      return null;
    }
  };

  // Force logout and clear all data (hard logout - logs out of Microsoft)
  const forceLogout = () => {
    setAccessToken(null);
    setError(null);
    setIsRefreshing(false);
    setSessionState('unauthenticated');
    setSessionMessage(null);
    
    // Clear all storage including session state
    sessionStorage.clear();
    localStorage.clear();
    
    // Force MSAL logout
    const accounts = msalInstance.getAllAccounts();
    if (accounts.length > 0) {
      msalInstance.logoutRedirect({
        account: accounts[0],
        postLogoutRedirectUri: window.location.origin
      });
    } else {
      // If no accounts, just redirect to login
      window.location.href = '/';
    }
  };

  // Soft logout - only clears app session, keeps Microsoft session
  const softLogout = () => {
    setAccessToken(null);
    setError(null);
    setIsRefreshing(false);
    setSessionState('logged_out');
    setSessionMessage('You have been logged out');
    
    // Clear only app-specific storage
    sessionStorage.removeItem('accessToken');
    // Store that user explicitly logged out
    sessionStorage.setItem('sessionState', 'logged_out');
    // Don't clear MSAL cache or call logoutRedirect
  };

  // Expire session due to inactivity
  const expireSession = () => {
    setAccessToken(null);
    setError(null);
    setIsRefreshing(false);
    setSessionState('expired');
    setSessionMessage('Your session expired due to inactivity');
    
    // Clear only app-specific storage
    sessionStorage.removeItem('accessToken');
    // Store that session expired
    sessionStorage.setItem('sessionState', 'expired');
  };

  // Initiate login manually
  const initiateLogin = async () => {
    try {
      const accounts = msalInstance.getAllAccounts();
      
      if (accounts.length > 0) {
        // Try silent login first if account exists
        const response = await msalInstance.acquireTokenSilent({
          account: accounts[0],
          scopes: [process.env.REACT_APP_AD_SCOPE!]
        });
        setAccessToken(response.accessToken);
        setSessionState('active');
        setSessionMessage(null);
        setError(null);
      } else {
        // No account, need interactive login
        await msalInstance.loginRedirect({
          scopes: [process.env.REACT_APP_AD_SCOPE!]
        });
      }
    } catch (error) {
      // If silent fails, try interactive
      try {
        await msalInstance.loginRedirect({
          scopes: [process.env.REACT_APP_AD_SCOPE!]
        });
      } catch (redirectError) {
        setError('Failed to initiate login');
      }
    }
  };

  // Clear error state
  const clearError = () => {
    setError(null);
  };

  // Add global error
  const showError = (message: string) => {
    setGlobalErrors(prev => [...prev, message]);
    // Auto-dismiss after 5 seconds
    setTimeout(() => {
      setGlobalErrors(prev => prev.slice(1));
    }, 5000);
  };

  // Dismiss specific error
  const dismissError = (index: number) => {
    setGlobalErrors(prev => prev.filter((_, i) => i !== index));
  };

  // Handle MSAL redirect on mount
  useEffect(() => {
    const handleRedirect = async () => {
      try {
        // Handle the redirect promise from MSAL
        const response = await msalInstance.handleRedirectPromise();
        
        if (response) {
          // User just came back from redirect
          setAccessToken(response.accessToken);
          setSessionState('active');
          setSessionMessage(null);
          setError(null);
        } else {
          // No redirect response, check if we can get token silently
          const accounts = msalInstance.getAllAccounts();
          
          // Only auto-login if state is 'unauthenticated' (first time visitor)
          // Do NOT auto-login if state is 'logged_out' (user explicitly logged out) or 'expired' (session timed out)
          if (accounts.length > 0 && !accessToken && sessionState === 'unauthenticated') {
            try {
              const silentResponse = await msalInstance.acquireTokenSilent({
                account: accounts[0],
                scopes: [process.env.REACT_APP_AD_SCOPE!]
              });
              setAccessToken(silentResponse.accessToken);
              setSessionState('active');
              setSessionMessage(null);
            } catch (silentError) {
              // Silent acquisition failed, user needs to login manually
            }
          }
        }
      } catch (error) {
        // Error handling redirect, user needs to login manually
      } finally {
        setIsHandlingRedirect(false);
      }
    };

    handleRedirect();
  }, []);

  // Update session state when access token changes
  useEffect(() => {
    if (accessToken && sessionState !== 'active') {
      setSessionState('active');
      setSessionMessage(null);
    }
  }, [accessToken]);

  const value = { 
    accessToken, 
    setAccessToken, 
    acquireToken, 
    refreshToken,
    forceLogout,
    softLogout,
    expireSession,
    initiateLogin,
    sessionState,
    sessionMessage,
    isRefreshing,
    isHandlingRedirect,
    error,
    clearError,
    showError,
    globalErrors,
    dismissError
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = (): AuthContextType => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
